import type { ReactNode } from 'react'

import { cn } from '@/lib/utils/cn'

/**
 * A single headline number.
 *
 * The number leads and the label follows beneath it, set small and quiet — a big number
 * with a small label is the conventional treatment because it is the correct one when the
 * number is genuinely the content. What is deliberately absent is a coloured gradient and
 * a percentage-change chip; neither is measured here, and inventing them would be decoration.
 */
export function StatTile({
  label,
  value,
  unit,
  hint,
  tone = 'default',
  footer,
}: {
  label: string
  value: ReactNode
  unit?: string
  hint?: string
  tone?: 'default' | 'ok' | 'fail' | 'denied' | 'waiting' | 'miss'
  footer?: ReactNode
}) {
  const tones = {
    default: 'text-ink',
    ok: 'text-ok',
    fail: 'text-fail',
    denied: 'text-denied',
    waiting: 'text-waiting',
    miss: 'text-miss',
  }

  return (
    <div className="border-b border-line px-4 py-3.5 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
      <p className="text-xs text-ink-muted" title={hint}>
        {label}
      </p>
      <p className="mt-1.5 flex items-baseline gap-1">
        <span className={cn('text-stat font-semibold tabular-nums', tones[tone])}>
          {value}
        </span>
        {unit ? <span className="text-sm text-ink-muted">{unit}</span> : null}
      </p>
      {footer ? <div className="mt-1.5 text-xs text-ink-muted">{footer}</div> : null}
    </div>
  )
}

export function StatRow({ children }: { children: ReactNode }) {
  return (
    <div className="panel grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
  )
}
